// src/composables/useTheme.js
import { ref, onMounted, computed } from 'vue';
import AnimatedGradientTexture from '@/components/textures/AnimatedGradientTexture.vue';
import NoisePattern from '@/components/textures/NoisePattern.vue';
import ImageTexture from '@/components/textures/ImageTexture.vue';
import gridPaper from '@/assets/textures/grid-paper.png';

export function useTheme() {
  const allThemes = ['default', 'cool-blue', 'radical', 'garamond'];
  const lockedTheme = 'garamond';
  const isThemeLocked = true;

  const availableThemes = isThemeLocked ? [lockedTheme] : allThemes;
  const storedDarkMode = localStorage.getItem('darkMode');

  if (!storedDarkMode) {
    localStorage.setItem('darkMode', 'light');
  }

  localStorage.setItem('theme', lockedTheme);

  const currentTheme = ref(lockedTheme);
  const isDark = ref(storedDarkMode === 'dark');

  const textureComponentMap = {
    'animated-gradient': {
      component: AnimatedGradientTexture,
      props: {},
    },
    'noise-pattern': {
      component: NoisePattern,
      props: {},
    },
    'grid-paper': {
      component: ImageTexture,
      props: {
        src: gridPaper,
        opacity: 0.2,
        mode: 'repeat',
      },
    },
    none: {
      component: null,
      props: {},
    },
  };

  const currentTextures = ref({
    background: { component: null, props: {} },
    card: { component: null, props: {} },
    button: { component: null, props: {} },
  });

  const applyTheme = () => {
    const root = document.documentElement;

    allThemes.forEach((theme) => {
      root.classList.remove(`${theme}-theme`);
    });
    root.classList.remove('dark-theme', 'light-theme');

    const activeTheme = isThemeLocked ? lockedTheme : currentTheme.value;
    root.classList.add(`${activeTheme}-theme`);
    root.classList.add(isDark.value ? 'dark-theme' : 'light-theme');

    currentTheme.value = activeTheme;
    localStorage.setItem('theme', activeTheme);
    localStorage.setItem('darkMode', isDark.value ? 'dark' : 'light');

    const styles = getComputedStyle(root);
    const bgKey = styles.getPropertyValue('--texture-background').replace(/['"]/g, '').trim();
    const cardKey = styles.getPropertyValue('--texture-card').replace(/['"]/g, '').trim();
    const buttonKey = styles.getPropertyValue('--textured-button').replace(/['"]/g, '').trim();

    currentTextures.value.background =
      textureComponentMap[bgKey] || textureComponentMap.none;
    currentTextures.value.card =
      textureComponentMap[cardKey] || textureComponentMap.none;
    currentTextures.value.button =
      textureComponentMap[buttonKey] || textureComponentMap.none;
  };

  const toggleDarkMode = () => {
    isDark.value = !isDark.value;
    applyTheme();
  };

  const setTheme = (theme) => {
    if (isThemeLocked) {
      currentTheme.value = lockedTheme;
      applyTheme();
      return;
    }
    if (availableThemes.includes(theme)) {
      currentTheme.value = theme;
      applyTheme();
    } else {
      console.warn(`Theme "${theme}" is not defined.`);
    }
  };

  onMounted(() => {
    applyTheme();
  });

  const computedClass = computed(() => ({
    container: 'ui-panel',
    headerFooter: 'ui-panel ui-panel--muted',
    button: 'ui-button ui-button--primary ui-button--md',
    input: 'ui-input ui-input--md',
    textarea: 'ui-textarea ui-textarea--md',
    select: 'ui-select ui-select--md',
    label: 'ui-label',
    footer: 'ui-footer',
    sidebar: 'ui-sidebar',
    link: 'ui-link',
    card: 'ui-card',
    text: isDark.value ? 'text-gray-300' : 'text-gray-700',
  }));

  return {
    isDark,
    currentTheme,
    availableThemes,
    isThemeLocked,
    computedClass,
    toggleDarkMode,
    setTheme,
    currentTextures,
  };
}
